import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query'
import { intentsApi } from '@/api/intents'
import { useTenantKey } from './useTenantKey'
import type { CreateIntentRequest, IntentFilters, PaginationParams, UpdateIntentRequest } from '@/types'
export type { BulkDeleteIntentsResponse } from '@/api/intents'

export function useIntents(projectId: string, pagination?: PaginationParams, filters?: IntentFilters) {
  const tenantKey = useTenantKey()
  const params = pagination || filters ? { ...pagination, ...filters } : undefined

  return useQuery({
    queryKey: tenantKey('projects', projectId, 'intents', params),
    queryFn: () => intentsApi.getAll(projectId, params),
    enabled: !!projectId,
    staleTime: 0,
    placeholderData: keepPreviousData,
  })
}

export function useIntentFeatures(projectId: string) {
  const tenantKey = useTenantKey()
  return useQuery({
    queryKey: tenantKey('projects', projectId, 'intents', 'features'),
    queryFn: () => intentsApi.getFeatures(projectId),
    enabled: !!projectId,
  })
}

export function useIntentEpics(projectId: string) {
  const tenantKey = useTenantKey()
  return useQuery({
    queryKey: tenantKey('projects', projectId, 'intents', 'epics'),
    queryFn: () => intentsApi.getEpics(projectId),
    enabled: !!projectId,
  })
}

export function useIntent(projectId: string, intentId: string | null) {
  const tenantKey = useTenantKey()
  return useQuery({
    queryKey: tenantKey('projects', projectId, 'intents', intentId!),
    queryFn: () => intentsApi.getById(projectId, intentId!),
    enabled: !!projectId && !!intentId,
  })
}

export function useCreateIntent(projectId: string) {
  const queryClient = useQueryClient()
  const tenantKey = useTenantKey()

  return useMutation({
    mutationFn: (request: CreateIntentRequest) => intentsApi.create(projectId, request),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: tenantKey('projects', projectId, 'intents') })
      queryClient.invalidateQueries({ queryKey: tenantKey('projects', projectId, 'intents', 'features') })
      queryClient.invalidateQueries({ queryKey: tenantKey('projects', projectId, 'intents', 'epics') })
    },
  })
}

export function useUpdateIntent(projectId: string) {
  const queryClient = useQueryClient()
  const tenantKey = useTenantKey()

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateIntentRequest }) =>
      intentsApi.update(projectId, id, data),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: tenantKey('projects', projectId, 'intents') })
      queryClient.invalidateQueries({ queryKey: tenantKey('projects', projectId, 'intents', 'features') })
      queryClient.invalidateQueries({ queryKey: tenantKey('projects', projectId, 'intents', 'epics') })
      queryClient.invalidateQueries({ queryKey: tenantKey('projects', projectId, 'intents', variables.id) })
    },
  })
}

export function useDeleteIntent(projectId: string) {
  const queryClient = useQueryClient()
  const tenantKey = useTenantKey()

  return useMutation({
    mutationFn: (id: string) => intentsApi.delete(projectId, id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: tenantKey('projects', projectId, 'intents') })
      queryClient.invalidateQueries({ queryKey: tenantKey('projects', projectId, 'intents', 'features') })
      queryClient.invalidateQueries({ queryKey: tenantKey('projects', projectId, 'intents', 'epics') })
      queryClient.invalidateQueries({ queryKey: tenantKey('projects', projectId, 'workflow-runs') })
      queryClient.invalidateQueries({ queryKey: tenantKey('projects', projectId, 'chain-runs') })
    },
  })
}

export function useBulkDeleteIntents(projectId: string) {
  const queryClient = useQueryClient()
  const tenantKey = useTenantKey()

  return useMutation({
    mutationFn: (ids: string[]) => intentsApi.bulkDelete(projectId, ids),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: tenantKey('projects', projectId, 'intents') })
      queryClient.invalidateQueries({ queryKey: tenantKey('projects', projectId, 'intents', 'features') })
      queryClient.invalidateQueries({ queryKey: tenantKey('projects', projectId, 'intents', 'epics') })
      queryClient.invalidateQueries({ queryKey: tenantKey('projects', projectId, 'workflow-runs') })
      queryClient.invalidateQueries({ queryKey: tenantKey('projects', projectId, 'chain-runs') })
    },
  })
}
